<template>
  <div class="participants">
    <div v-if="participants && participants.length > 0">
      <v-container>
        <v-row v-if="game.state == 'created' || game.state == 'checkin'">
          <v-col cols="12">
            <v-btn
              :disabled="loading || !invitationAllowed"
              @click="openInvitePlayersDialog()"
              class="secondary"
            >
              <v-icon left>mdi-account-multiple-plus</v-icon>{{$t('common.invite-friends')}}
            </v-btn>
          </v-col>
          <friends-selector-dialog
            :visible="showInvitePlayersDialog"
            :users="invitablePlayers"
            :closeAction="function() {showInvitePlayersDialog = false}"
            :acceptAction="inviteFriendsDialogAccepted"
          />
          <v-col cols="12">
            <v-btn
              :disabled="loading || !invitationAllowed"
              @click="openInviteSpectatorsDialog()"
              class="secondary"
            >
              <v-icon left>mdi-eye-plus-outline</v-icon>{{$t('common.invite-spectators')}}
            </v-btn>
          </v-col>
          <friends-selector-dialog
            :visible="showInviteSpectatorsDialog"
            :users="invitablePlayers"
            :closeAction="function() {showInviteSpectatorsDialog = false}"
            :acceptAction="inviteSpectatorsDialogAccepted"
          />

          <v-col cols="12">
            <invitation-widget :game="game" :disabled="!invitationAllowed"></invitation-widget>
          </v-col>
        </v-row>
        <v-row v-if="activePlayers.length >= 10">
          <v-col>
            <span class="overline">{{$tc('participants.participants-default', activePlayers.length, {num: activePlayers.length})}}</span>
          </v-col>
        </v-row>
        <v-row v-for="player in activePlayers" v-bind:key="player.uid">
          <v-col cols="12">
            <user-widget :user="player" :game="game" :actions="participantActions(player)" :actionsDisabled="$store.state.isLoading" :showDefaultActions="['details']">
              <template v-slot:beforeActionButtons>
                <v-icon left :color="player.checkedIn ? 'success' : ''" v-if="game.state == 'checkin'">mdi-bag-checked</v-icon>
              </template>
            </user-widget>
          </v-col>
        </v-row>
        <v-row>
          <v-col v-if="game.maxPlayers">
            <span v-if="game.maxPlayers > activePlayers.length" class="overline">{{$t('participants.participants-with-max', {num: activePlayers.length, max: game.maxPlayers})}}</span>
            <span v-else class="overline">{{$tc('participants.participants-with-max-reached', activePlayers.length, {num: activePlayers.length})}}</span>
          </v-col>
          <v-col v-else>
            <span class="overline">{{$tc('participants.participants-default', activePlayers.length, {num: activePlayers.length})}}</span>
          </v-col>
        </v-row>
        <v-row v-if="game.premiumOnly === true">
          <v-col>
            <span class="overline">&lt;{{$t('competition.premium-only')}}&gt;</span>
          </v-col>
        </v-row>
        <div v-if="spectators.length > 0">
          <v-row v-for="player in spectators" v-bind:key="player.uid">
            <v-col cols="12">
              <user-widget :user="player" :game="game" :actions="participantActions(player)" :actionsDisabled="$store.state.isLoading" :showDefaultActions="['details']" />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <span class="overline">{{$tc('participants.spectators', activePlayers.length, {num: spectators.length})}}</span>
            </v-col>
          </v-row>
        </div>
      </v-container>
    </div>
    <div
      v-else-if="participants && participants.length == 0"
    >Keine Teilnehmer gefunden (das sollte nie auftreten)</div>
    <drop-player-dialog
      v-if="dropPlayer"
      :player="dropPlayer"
      :closeAction="closeDropPlayerDialog"
      :confirmAction="confirmedDropPlayer"
    />
  </div>
</template>

<script>
import FriendsSelectorDialog from "@/widgets/FriendsSelectorDialog";
import {
  EVENT_REFRESH_DATA
} from "@/plugins/eventbus";
import { MUTATION_RAISE_NOTIFICATION } from "@/plugins/store";
import InvitationWidget from "@/widgets/InvitationWidget";
import DropPlayerDialog from '@/widgets/tools/DropPlayerDialog.vue';

export default {
  name: "Participants",
  components: {
    FriendsSelectorDialog,
    InvitationWidget,
    DropPlayerDialog,
  },
  props: {
    game: {
      type: Object,
      required: true
    }
  },
  data: function() {
    return {
      loading: false,
      participants: undefined,
      invitablePlayers: [],
      showInvitePlayersDialog: false,
      showInviteSpectatorsDialog: false,
      dropPlayer: undefined,
    };
  },
  computed: {
    invitationAllowed: function() {
      return this.game.youAreAdmin;
    },
    activePlayers: function() {
      return this.participants.filter(p => {
        return p.isSpectator !== true;
      });
    },
    spectators: function() {
      return this.participants.filter(p => {
        return p.isSpectator === true;
      });
    },
  },
  watch: {
    game: function(val, oldVal) {
      this.participants = val.participants;
      if (val.uid != oldVal.uid) {
        console.log("game changed", val, oldVal);
      }
    }
  },
  mounted() {
    this.participants = this.game.participants;
    this.$eventBus.$on(EVENT_REFRESH_DATA, this.onRefreshData);
  },
  beforeDestroy() {
    this.$eventBus.$off(EVENT_REFRESH_DATA, this.onRefreshData);
  },
  methods: {
    onRefreshData() {},
    openInvitePlayersDialog() {
      this.loading = true;
      this.$api.getGameInvitablePlayers(this.game.uid).then(response => {
        this.loading = false;
        this.invitablePlayers = response.data.list;
        this.showInvitePlayersDialog = true;
      })
      .catch(error => {
        this.loading = false;
        this.$common.onError(error)
      });      
    },
    openInviteSpectatorsDialog() {
      this.loading = true;
      this.$api.getGameInvitablePlayers(this.game.uid).then(response => {
        this.loading = false;
        this.invitablePlayers = response.data.list;
        this.showInviteSpectatorsDialog = true;
      })
      .catch(error => {
        this.loading = false;
        this.$common.onError(error)
      });      
    },
    inviteFriendsDialogAccepted(selectedFriends) {
      if (selectedFriends.length == 0) {
        return;
      }
      let friendUIDsToInvite = selectedFriends.map(friend => {
        return friend.uid;
      });
      this.$api.inviteUsersToGameInstance(this.game.uid, friendUIDsToInvite).then(() => {
          this.loading = false;
          this.$store.commit(MUTATION_RAISE_NOTIFICATION, {
            message: this.$tc('common.num-players-invited', selectedFriends.length, {num: selectedFriends.length}),
            type: "success"
          });
        })
        .catch(error => {
          this.loading = false;
          this.$common.onError(error)
        });
    },
    inviteSpectatorsDialogAccepted(selectedFriends) {
      if (selectedFriends.length == 0) {
        return;
      }
      let friendUIDsToInvite = selectedFriends.map(friend => {
        return friend.uid;
      });
      this.$api.inviteSpectatorsToGameInstance(this.game.uid, friendUIDsToInvite).then(() => {
          this.loading = false;
          this.$store.commit(MUTATION_RAISE_NOTIFICATION, {
            message: this.$tc('common.num-spectators-invited', selectedFriends.length, {num: selectedFriends.length}),
            type: "success"
          });
        })
        .catch(error => {
          this.loading = false;
          this.$common.onError(error)
        });
    },
    closeDropPlayerDialog() {
      this.dropPlayer = undefined;
    },
    confirmedDropPlayer(user) {
      console.log('confirmed drop player', user)
      this.loading = true;
      this.$api.dropPlayer(this.game.uid, user.uid).then(() => {
        this.loading = false;
        this.$store.commit(MUTATION_RAISE_NOTIFICATION, {
          message: this.$t('common.player-dropped-successfully', {name: user.name}),
          type: "success"
        });
        this.$eventBus.$emit(EVENT_REFRESH_DATA);
      })
      .catch(error => {
        this.loading = false;
        this.$common.onError(error)
      });
    },
    participantActions: function(player) {
      let me = this;
      let ret = {}
      if (this.game.youAreAdmin) {
        if (this.game.state == 'created' || this.game.state == 'checkin') {
          if (player.isSpectator == false) {
            ret.makeSpectator = {
              icon: "mdi-eye",
              label: "Spieler zum Zuschauer ändern",
              action: function(user) {
                me.$api
                  .patchGameInstanceParticipant(me.game.uid, user.uid, {setSpectator: true}).then(() => {
                    me.$store.commit(MUTATION_RAISE_NOTIFICATION, {
                      message: `Spieler "${user.name}" ist nun ein Zuschauer`,
                      type: "success"
                    });
                    me.$eventBus.$emit(EVENT_REFRESH_DATA);
                  })
                  .catch(error => {
                    me.$common.onError(error)
                  });                
              }
            }
          } else {
            ret.unsetSpectator = {
              icon: "mdi-eye",
              label: "Zuschauer zu Teilnehmer ändern",
              action: function(user) {
                me.$api
                  .patchGameInstanceParticipant(me.game.uid, user.uid, {setSpectator: false}).then(() => {
                    me.$store.commit(MUTATION_RAISE_NOTIFICATION, {
                      message: `Spieler "${user.name}" ist nun ein Teilnehmer`,
                      type: "success"
                    });
                    me.$eventBus.$emit(EVENT_REFRESH_DATA);
                  })
                  .catch(error => {
                    me.$common.onError(error)
                  });                
              }
            }
          }
        }
        if (this.game.state != 'finished') {
          if (player.isAdmin == false) {
            ret.makeAdmin = {
              icon: "mdi-hammer-wrench",
              label: "Spieler Admin-Rechte erteilen",
              action: function(user) {
                me.$api
                  .patchGameInstanceParticipant(me.game.uid, user.uid, {setAdmin: true}).then(() => {
                    me.$store.commit(MUTATION_RAISE_NOTIFICATION, {
                      message: `Spieler "${user.name}" hat nun Admin-Rechte`,
                      type: "success"
                    });
                    me.$eventBus.$emit(EVENT_REFRESH_DATA);
                  })
                  .catch(error => {
                    me.$common.onError(error)
                  });                
              }
            }
          } else {
            ret.unsetAdmin = {
              icon: "mdi-hammer-wrench",
              classes: "error",
              label: "Admin-Rechte entfernen",
              action: function(user) {
                me.$api.patchGameInstanceParticipant(me.game.uid, user.uid, {setAdmin: false}).then(() => {
                  me.$store.commit(MUTATION_RAISE_NOTIFICATION, {
                    message: `Spieler "${user.name}" hat nun keine Admin-Rechte mehr`,
                    type: "success"
                  });
                  me.$eventBus.$emit(EVENT_REFRESH_DATA);
                })
                .catch(error => {
                  me.$common.onError(error)
                });                
              }
            }
          }
        }
        if (this.game.state != 'finished') {
          ret.dropPlayer = {
            icon: "mdi-karate",
            classes: "error",
            label: "Spieler rauswerfen",
            action: function(user) {
              me.dropPlayer = user
            }
          }
        }
      }
      return ret
    },
  },
};
</script>
